/**
 * AppNavbar.tsx — Barra de navegación superior
 *
 * Barra clara (blanco) con el logo de SisNetel a la izquierda (igual estilo
 * que la pantalla de Login), un botón de Soporte a la derecha, y un menú
 * hamburguesa desplegable con "Inicio", todas las opciones según el rol
 * (admin/cliente) y "Cerrar sesión" al final.
 *
 * Se usa en: Menu, Clientes, Tickets, y otras pantallas internas.
 * El paddingTop tiene en cuenta la "safe area" del teléfono (el notch o la barra de estado).
 */

import React, { useState } from 'react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaHeadset, FaBars, FaHome, FaSignOutAlt, FaMoneyCheckAlt, FaTools, FaPlug,
  FaUsers, FaMoneyBillWave, FaTicketAlt, FaWifi, FaTv, FaUserPlus,
  FaFileInvoice, FaKey, FaReceipt,
} from 'react-icons/fa';
import AyudaModal from './AyudaModal';
import InstalacionesModal from './InstalacionesModal';
import logoIso from '../assets/sisnetelp.png';
import './AppNavbar.css';

type NavItem = { key: string; label: string; icon: React.FC; onClick: () => void };

const AppNavbar: React.FC = () => {
  const navigate = useNavigate();
  const [showAyuda, setShowAyuda] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [showProximamente, setShowProximamente] = useState(false);
  const [proximamenteOpcion, setProximamenteOpcion] = useState('');

  const HeadsetIcon = FaHeadset as unknown as React.FC;
  const MenuIcon    = FaBars    as unknown as React.FC;
  const HomeIcon    = FaHome    as unknown as React.FC;
  const LogoutIcon  = FaSignOutAlt as unknown as React.FC;
  const MoneyIcon   = FaMoneyCheckAlt as unknown as React.FC;
  const InstallIcon = FaTools as unknown as React.FC;
  const PlugIcon    = FaPlug as unknown as React.FC;
  const UsersIcon   = FaUsers as unknown as React.FC;
  const AbonoIcon   = FaMoneyBillWave as unknown as React.FC;
  const TicketIcon  = FaTicketAlt as unknown as React.FC;
  const WifiIcon    = FaWifi as unknown as React.FC;
  const TvIcon      = FaTv as unknown as React.FC;
  const SellerIcon  = FaUserPlus as unknown as React.FC;
  const InvoiceIcon = FaFileInvoice as unknown as React.FC;
  const KeyIcon     = FaKey as unknown as React.FC;
  const ReceiptIcon = FaReceipt as unknown as React.FC;

  const clientDataJSON = localStorage.getItem('clientData');
  const clientData = clientDataJSON ? JSON.parse(clientDataJSON) : null;
  const showAdmin = Boolean(clientData?.token_cliente?.trim());

  // Clave del campo personalizado donde se guarda el OID de GenieACS (con el que se consulta el servicio)
  const CLAVE_OID_GENIEACS = '1770612677';
  const tieneOidGenieAcs = (srv: any): boolean => {
    try {
      const personalizados = typeof srv.personalizados === 'string' ? JSON.parse(srv.personalizados) : (srv.personalizados || {});
      return Boolean(personalizados[CLAVE_OID_GENIEACS]);
    } catch { return false; }
  };
  const showChangePwd = Boolean(clientData?.servicios?.some(tieneOidGenieAcs));

  const handleLogout = () => {
    localStorage.removeItem('clientData');
    navigate('/');
  };

  const handleProximamente = (opcion: string) => {
    setProximamenteOpcion(opcion);
    setShowProximamente(true);
  };

  // Todas las opciones de la app, según el rol (mismo criterio que BottomNav)
  const allItems: NavItem[] = clientData
    ? showAdmin
      ? [
          // "Pago Clientes", "Instalaciones" y "Promesa de pago" ya están en la barra inferior, no se repiten aquí
          { key: 'clientes',     label: 'Mis Clientes',         icon: UsersIcon,   onClick: () => navigate('/menu/clientes') },
          { key: 'abono',        label: 'Abono a Factura',      icon: AbonoIcon,   onClick: () => navigate('/menu/abono') },
          { key: 'comprobantes', label: 'Subir Comprobante',    icon: ReceiptIcon, onClick: () => navigate('/menu/comprobantes') },
          { key: 'tickets',      label: 'Mis Tickets',          icon: TicketIcon,  onClick: () => navigate('/menu/tickets') },
          { key: 'pagar',        label: 'Pagar Ahora',          icon: MoneyIcon,   onClick: () => navigate(`/r/${btoa(clientData.cedula)}`) },
          ...(showChangePwd
            ? [{ key: 'clave', label: 'Cambiar Contraseña', icon: KeyIcon, onClick: () => navigate('/menu/wifi') } as NavItem]
            : []),
          { key: 'soporte',      label: 'Soporte Técnico',      icon: HeadsetIcon, onClick: () => setShowAyuda(true) },
          { key: 'promoNet',     label: 'Promociones Internet', icon: WifiIcon,    onClick: () => navigate('/menu/promociones-internet') },
          { key: 'promoTv',      label: 'Promociones TV',       icon: TvIcon,      onClick: () => handleProximamente('Promociones TV') },
          { key: 'vendedor',     label: 'Quiero ser vendedor',  icon: SellerIcon,  onClick: () => handleProximamente('Quiero ser vendedor') },
        ]
      : [
          { key: 'pagar',    label: 'Pagar Ahora', icon: MoneyIcon,   onClick: () => navigate(`/r/${btoa(clientData.cedula)}`) },
          { key: 'facturas', label: 'Facturas',    icon: InvoiceIcon, onClick: () => navigate('/clientes/facturas') },
          ...(showChangePwd
            ? [{ key: 'clave', label: 'Cambiar Contraseña', icon: KeyIcon, onClick: () => navigate('/menu/wifi') } as NavItem]
            : []),
          { key: 'soporte',  label: 'Soporte Técnico', icon: HeadsetIcon, onClick: () => setShowAyuda(true) },
          { key: 'promoNet', label: 'Promociones Internet', icon: WifiIcon, onClick: () => navigate('/menu/promociones-internet') },
          { key: 'promoTv',  label: 'Promociones TV',       icon: TvIcon,   onClick: () => handleProximamente('Promociones TV') },
          { key: 'vendedor', label: 'Quiero ser vendedor',  icon: SellerIcon, onClick: () => handleProximamente('Quiero ser vendedor') },
        ]
    : [];

  return (
    <>
      <div
        className="an-bar"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 8px)' }}
      >
        {/* Logo + nombre, igual estilo que el Login */}
        <div className="an-brand" onClick={() => navigate('/menu')}>
          <img src={logoIso} alt="SisNetel" className="an-logo-img" />
          <div>
            <div className="an-brand-name">SISNETEL</div>
            <div className="an-brand-sub">FIBER EC</div>
          </div>
        </div>

        {/* Acciones a la derecha */}
        <div className="an-actions">
          <button
            className="an-icon-btn"
            aria-label="Soporte técnico"
            onClick={() => setShowAyuda(true)}
          >
            <HeadsetIcon />
          </button>

          <Dropdown align="end">
            <Dropdown.Toggle as="button" className="an-icon-btn an-icon-btn--menu" id="an-menu-toggle">
              <MenuIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu className="an-menu">
              <Dropdown.Item onClick={() => navigate('/menu')}>
                <span className="an-menu-item"><HomeIcon /> Inicio</span>
              </Dropdown.Item>

              {allItems.length > 0 && <Dropdown.Divider />}
              {allItems.map(item => {
                const Icon = item.icon;
                return (
                  <Dropdown.Item key={item.key} onClick={item.onClick}>
                    <span className="an-menu-item"><Icon /> {item.label}</span>
                  </Dropdown.Item>
                );
              })}

              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout} className="an-menu-item--danger">
                <span className="an-menu-item"><LogoutIcon /> Cerrar sesión</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <AyudaModal show={showAyuda} onHide={() => setShowAyuda(false)} />
      <InstalacionesModal show={showInstall} onHide={() => { setShowInstall(false); window.location.reload(); }} />

      <Modal show={showProximamente} onHide={() => setShowProximamente(false)} centered>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: 72 }}>🚀</div>
          <h4 className="mt-3 fw-bold">¡Próximamente!</h4>
          <p className="text-muted mt-2">
            Estamos trabajando en la opción<br />
            <strong>{proximamenteOpcion}</strong><br />
            para ofrecerte la mejor experiencia.<br />
            ¡Muy pronto disponible!
          </p>
          <div style={{ fontSize: 24 }}>⚙️ 🛠️ ✨</div>
          <Button variant="primary" className="mt-3 px-4" onClick={() => setShowProximamente(false)}>
            Entendido
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AppNavbar;
